import React, { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import { fileToHeadshotDataUrl } from '../utils/headshot';
import AthleteHeadshot from './AthleteHeadshot';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Upload or clear an athlete headshot. The browser sends a cropped JPEG data
 * URL; the API stores it in Vercel Blob (or local /uploads) and returns the
 * public URL. Coaches can update anyone in their club; athletes can update
 * their own linked profile.
 */
export default function HeadshotUploader({ athlete, onUpdated, compact = false }) {
  const { token } = useAuth();
  const inputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(athlete?.headshot_url || null);

  const currentUrl = preview ?? athlete?.headshot_url ?? null;

  const saveHeadshot = async (headshotUrl) => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/athletes/${athlete.id}/headshot`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ headshotUrl })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save headshot');
      }
      const nextUrl = data.athlete?.headshot_url || null;
      setPreview(nextUrl);
      onUpdated?.({ ...athlete, headshot_url: nextUrl });
    } catch (err) {
      setError(err.message || 'Failed to save headshot');
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await fileToHeadshotDataUrl(file);
      await saveHeadshot(dataUrl);
    } catch (err) {
      setError(err.message || 'Could not process that image');
    }
  };

  return (
    <div className={compact ? 'flex items-center gap-3' : 'flex flex-col sm:flex-row sm:items-center gap-4'}>
      <AthleteHeadshot name={athlete?.name} headshotUrl={currentUrl} size={compact ? 'md' : 'lg'} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-700">Headshot</p>
        <p className="text-xs text-gray-500 mb-2">Square crop, used on the team roster.</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={saving || !athlete?.id}
            className="btn-theme-secondary px-3 py-1.5 rounded text-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : currentUrl ? 'Replace photo' : 'Upload photo'}
          </button>
          {currentUrl && (
            <button
              type="button"
              onClick={() => saveHeadshot(null)}
              disabled={saving}
              className="px-3 py-1.5 rounded text-sm text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
            >
              Remove
            </button>
          )}
        </div>
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={handleFile}
        />
      </div>
    </div>
  );
}
